'use client';

import { Input } from '#/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '#/components/ui/select';
import { OrderStatusEnum } from '#/constants/order-status';
import { t, useTranslation } from 'intl-lib';
import { isEqual } from 'lodash-es';
import { ChangeEvent, useMemo } from 'react';

export interface SearchParams {
  searchKey?: string;
  keywords?: string;
}

interface Props {
  searchParams?: SearchParams;
  onSearchParamsChange(SearchParams: SearchParams): void;
  filterStatus?: OrderStatusEnum[];
  onFilterStatusChange(filterStatus: OrderStatusEnum[]): void;
}

export function Toolbar({
  searchParams,
  onSearchParamsChange,
  filterStatus,
  onFilterStatusChange,
}: Props) {
  return (
    <div className="flex-1 flex flex-col @3xl/main:flex-row @3xl/main:items-center justify-between gap-2">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchFilter
          searchParams={searchParams}
          onSearchParamsChange={onSearchParamsChange}
        />
        <FacetedFilter
          filterStatus={filterStatus}
          onFilterStatusChange={onFilterStatusChange}
        />
      </div>
    </div>
  );
}

interface SearchFilterProps {
  searchParams?: SearchParams;
  onSearchParamsChange(searchParams: SearchParams): void;
}
export function SearchFilter({
  searchParams,
  onSearchParamsChange,
}: SearchFilterProps) {
  const searchBy = useTranslation('my_staking.order.toolbar.search_by');
  const options = useMemo(
    () => [
      {
        label: searchBy.claim_address.label,
        value: 'claimaddr',
        placeholder: searchBy.claim_address.placeholder,
      },
      {
        label: searchBy.withdrawal_address.label,
        value: 'withdrawaddr',
        placeholder: searchBy.withdrawal_address.placeholder,
      },
    ],
    [searchBy]
  );

  const option = useMemo(
    () => options.find(option => option.value == searchParams?.searchKey),
    [options, searchParams?.searchKey]
  );

  const onSearchKeyChange = (value: string) => {
    const searchKey = value;
    const keywords = searchParams?.keywords;
    onSearchParamsChange({ searchKey, keywords });
  };
  const onKeywordsChange = (event: ChangeEvent<HTMLInputElement>) => {
    const searchKey = searchParams?.searchKey;
    const keywords = event.target.value;
    onSearchParamsChange({ searchKey, keywords });
  };

  return (
    <div className="flex-1 flex items-center gap-2">
      <Select value={searchParams?.searchKey} onValueChange={onSearchKeyChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select a searchKey" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option, i) => (
            <SelectItem key={i} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="search"
        placeholder={option?.placeholder}
        value={searchParams?.keywords}
        onChange={onKeywordsChange}
        className="min-w-40 max-w-96 w-full"
      />
    </div>
  );
}

interface FacetedFilterProps {
  filterStatus?: OrderStatusEnum[];
  onFilterStatusChange(filterStatus: OrderStatusEnum[]): void;
}
export function FacetedFilter({
  filterStatus,
  onFilterStatusChange,
}: FacetedFilterProps) {
  const options = useMemo(
    () => [
      {
        label: t('my_staking.order.order_status.all'),
        value: [],
      },
      {
        label: t('my_staking.order.order_status.waiting_for_payment'),
        value: [
          OrderStatusEnum.WAITING_FOR_PAYMENT,
          OrderStatusEnum.PAYMENT_FAILED,
        ],
      },
      {
        label: t('my_staking.order.order_status.pending'),
        value: [OrderStatusEnum.PENDING],
      },
      {
        label: t('my_staking.order.order_status.active'),
        value: [
          OrderStatusEnum.ACTIVE,
          OrderStatusEnum.SLASHED,
          OrderStatusEnum.EXITING,
        ],
      },
      {
        label: t('my_staking.order.order_status.exited'),
        value: [OrderStatusEnum.EXIT],
      },
    ],
    []
  );

  const value = useMemo(
    () => options.findIndex(option => isEqual(option.value, filterStatus)),
    [options, filterStatus]
  );

  const onValueChange = (value: string) => {
    const index = Number(value);
    const filterStatus = options.at(index)?.value ?? [];
    onFilterStatusChange(filterStatus);
  };
  return (
    <Select value={String(value)} onValueChange={onValueChange}>
      <SelectTrigger className="w-45">
        <SelectValue placeholder="Select a status" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option, i) => (
          <SelectItem key={i} value={String(i)}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
